import { Component, OnDestroy, OnInit } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { Router } from "@angular/router";
import { FileLogger } from "@comunicare/comunicare-helper";
import {
  ACTION_TARGET,
  DataType,
  ICareplan,
  ICareplanKnowledgeInfos,
  KNOW_CATEGORY,
  KNOW_DOC_CATEGORY,
} from "@comunicare/comunicare-interfaces";
import { TranslateService } from "@ngx-translate/core";
import { forkJoin, of, Subject } from "rxjs";
import { delay, first, skipWhile, takeUntil } from "rxjs/operators";
import { AVAILABLE_ACTIONS } from "src/app/careplan-editor/data/careplan-editor.enum";
import { CareplanEditorService } from "src/app/careplan-editor/domain/careplan-editor.service";
import { IlinkableKnowledgeItem } from "src/app/careplan-editor/domain/IlinkableItem.interface";
import { LinkableKnowledgeItem } from "src/app/careplan-editor/domain/linkable-item.model";
import { KnowledgeCreationDialogComponent } from "src/app/components/forms/add-knowledge/add-knowledge.component";
import { CareplansService } from "src/app/providers/careplans.service";
import { SessionService } from "src/app/providers/session.service";

@Component({
  selector: "app-careplan-editor-faq-tab",
  templateUrl: "./careplan-editor-faq-tab.component.html",
  styleUrls: ["./careplan-editor-faq-tab.component.scss"],
})
export class CareplanEditorFaqTabComponent implements OnInit, OnDestroy {
  public careplanTemplate: ICareplan;
  /** knowledges actually linked to the careplan (target table) */
  public link2CareplanKnowledges: IlinkableKnowledgeItem[];
  public columns = ["term", "category", "type"];
  public actions = [AVAILABLE_ACTIONS.unlink, AVAILABLE_ACTIONS.edit];
  public filters = [
    {
      propertyName: "category",
      dataType: DataType.CHOICE,
      data: {
        value: [
          KNOW_CATEGORY.MEDICAL,
          KNOW_CATEGORY.PATHOLOGY,
          KNOW_CATEGORY.THERAPY,
          KNOW_CATEGORY.DRUG,
          KNOW_CATEGORY.DEVICE,
          KNOW_CATEGORY.OBSERVATION,
        ],
      },
    },
    {
      propertyName: "type",
      dataType: DataType.CHOICE,
      data: {
        value: [KNOW_DOC_CATEGORY.FAQ],
      },
    },
  ];
  public currentLanguage: string = this.sessionService.userLang;
  private knowledgeIdToLink: string;
  private onDestroy$ = new Subject<void>();
  public isLoading: boolean;

  constructor(
    private translateService: TranslateService,
    public careplanEditorService: CareplanEditorService,
    private sessionService: SessionService,
    private dialog: MatDialog,
    private router: Router,
    private snackBar: MatSnackBar,
    private careplanService: CareplansService
  ) {
    const navigation = this.router.getCurrentNavigation();
    const knowledgeId = navigation?.extras?.state?.["knowledgeId"];

    if (knowledgeId) {
      this.knowledgeIdToLink = knowledgeId;
    }
  }

  ngOnInit(): void {
    // Refresh translation when needed
    this.sessionService.refreshServerTraductions.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
      this.currentLanguage = this.sessionService.userLang;
    });
    // Observable to check if the careplanTemplate is ready
    const careplanTemplateReady$ = this.careplanEditorService.currentCareplanTemplate
      ? of(true)
      : this.careplanEditorService.careplanTemplateReady$.pipe(
          skipWhile((value) => value === false),
          first()
        );

    careplanTemplateReady$.subscribe(() => {
      this.careplanTemplate = this.careplanEditorService.currentCareplanTemplate;
      this.setupFaqs();
    });
  }

  ngOnDestroy(): void {
    // After publish careplanTemplateReady$ value is false to avoid trying to save a careplan without draft
    if (this.careplanEditorService.careplanTemplateReady$.value === true) {
      this.careplanEditorService.save(false, false, false);
    }
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  public async update($event: { item: LinkableKnowledgeItem; items: LinkableKnowledgeItem[]; action: AVAILABLE_ACTIONS }): Promise<void> {
    const { item, action } = $event;

    if (item.origin === "link2careplan") {
      if (action === AVAILABLE_ACTIONS.unlink) {
        await this.handleUnlinkFromCareplan(item);
      } else {
        await this.handleLinkToCareplan(item);
      }
    }
    this.setupFaqs();
  }

  public async handleLinkToCareplan(item: LinkableKnowledgeItem): Promise<void> {
    // get the faq from the link2careplan
    const faqFromLink2Careplan = new Set(this.careplanEditorService.link2CareplanForm.get("knowledges").value as string[]);
    // add the faq to the link2careplan
    faqFromLink2Careplan.add(item.uniqueId);
    await this.updateLink2Careplan(Array.from(faqFromLink2Careplan));
  }

  public async handleUnlinkFromCareplan(item: LinkableKnowledgeItem): Promise<void> {
    // get the faq from the link2careplan
    const faqFromLink2Careplan = new Set(this.careplanEditorService.link2CareplanForm.get("knowledges").value as string[]);
    // remove the faq from the link2careplan
    faqFromLink2Careplan.delete(item.uniqueId);
    await this.updateLink2Careplan(Array.from(faqFromLink2Careplan));
  }

  public async updateLink2Careplan(knowledgeIds: string[]): Promise<void> {
    this.careplanEditorService.link2CareplanForm.patchValue({ knowledges: knowledgeIds });
    try {
      await this.careplanService.updateLink2Careplan(this.careplanEditorService.link2CareplanForm.getRawValue()).toPromise();
    } catch (error) {
      this.handleError();
    }
  }

  /**
   * Show toast and reset knowledge in case of error
   */
  public handleError(): void {
    this.snackBar.open(this.translateService.instant("api.errors.server-error"), "ok", { duration: 5000 });
    // reload the faq list in case of error
    this.setupFaqs();
  }

  /**
   * Filters ICareplanKnowledgeInfos[] by origin and maps them to IlinkableKnowledgeItem[] array
   * @param allKnowledge
   * @param origin
   */
  public mapAvailableFaqs(allFaqs: ICareplanKnowledgeInfos[], origin: "link2careplan" | "activity"): IlinkableKnowledgeItem[] {
    return allFaqs
      .filter((k) => k.origin === origin)
      .map((k) => {
        return new LinkableKnowledgeItem({
          uniqueId: k.identifier,
          term: k.term,
          category: this.translateService.instant("knowledgebase.category." + k.category),
          type: this.translateService.instant("knowledgebase.doccategory." + k.type),
          originReference: k.originReference,
          origin: k.origin,
          itemType: ACTION_TARGET.KNOWLEDGE,
          identifier: k.identifier,
        });
      });
  }

  public addFaq(): void {
    this.dialog.open(KnowledgeCreationDialogComponent, {
      data: { previousLocation: this.router.url },
      disableClose: false,
    });
  }

  /**
   * Sets up faq related to the careplan.
   * Fetches careplanKnowledgeInfos and maps the available knowledge to link2CareplanKnowledges
   */
  public setupFaqs(): void {
    this.isLoading = true;

    const knowledgeRequest = this.careplanEditorService
      .getCareplanKnowledgeInfos(
        this.careplanTemplate.support[0].reference,
        true,
        [KNOW_DOC_CATEGORY.FAQ],
        this.careplanTemplate.publicationDate
      )
      .pipe(first(), delay(500));

    // Update the link2Careplan since linking a knowledge to an activity change the link2careplan in the backend
    const link2CareplanRequest = this.careplanEditorService.getLink2Careplan(this.careplanTemplate.support[0].reference, true);

    forkJoin([knowledgeRequest, link2CareplanRequest]).subscribe({
      next: ([knowledges, link2Careplan]) => {
        this.link2CareplanKnowledges = this.mapAvailableFaqs(knowledges, "link2careplan");
        this.careplanEditorService.link2CareplanForm.patchValue(link2Careplan.draft);

        if (this.knowledgeIdToLink) {
          this.linkKnowledgeFromState(this.knowledgeIdToLink);
        }
      },
      error: (error) => {
        FileLogger.error("CareplanEditorFaqTabComponent - setupFaqs ", "Error fetching knowledge data", error);
      },
      complete: () => {
        this.isLoading = false;
      },
    });
  }

  /**
   * Links a knowledge from the navigation state to the careplan
   */
  private async linkKnowledgeFromState(knowledgeId: string): Promise<void> {
    const currentKnowledges = new Set(this.careplanEditorService.link2CareplanForm.get("knowledges").value as string[]);

    // Add the knowledge if it's not already linked
    if (!currentKnowledges.has(knowledgeId)) {
      currentKnowledges.add(knowledgeId);
      await this.updateLink2Careplan(Array.from(currentKnowledges));
      this.knowledgeIdToLink = null;
      this.setupFaqs();
    }
  }
}
